import { errorBox } from '@core/components/AlertBox';
import { useKeys } from '@core/logic';
import { useFrame, useThree } from '@react-three/fiber';
import { Object3dNames } from '@viewer3D/types';
import { FC, useEffect, useRef, useState } from 'react';
import type { Event } from 'three';
import { Frustum, Matrix4, Vector3, Euler, Object3D } from 'three';
import { TransformControls } from 'three-stdlib';
import { proxy, useSnapshot } from 'valtio';

interface TransformControlsPublic extends Object3D {
  enabled: boolean;
  visible: boolean;
  mode: 'translate' | 'rotate' | 'scale';
  object?: Object3D;
  camera: import('three').Camera;
  detach: () => this;
  reset: () => this;
  dispose: () => void;
}

const addListener = (
  target: TransformControlsPublic,
  type: string,
  listener: (event: Event) => void,
) => {
  (
    target as unknown as { addEventListener: (t: string, l: (e: Event) => void) => void }
  ).addEventListener(type, listener);
};

const removeListener = (
  target: TransformControlsPublic,
  type: string,
  listener: (event: Event) => void,
) => {
  (
    target as unknown as { removeEventListener: (t: string, l: (e: Event) => void) => void }
  ).removeEventListener(type, listener);
};

type EventHandler = (event: Event) => void;

interface TransformControlState {
  objectName?: string;
  onAfterRotate?: EventHandler;
  onAfterTranslate?: EventHandler;
  onBeforeActivate?: (mode: 'translate' | 'rotate', proceed: () => void) => void;
}

const state = proxy<TransformControlState>({
  objectName: undefined,
  onAfterTranslate: undefined,
  onAfterRotate: undefined,
  onBeforeActivate: undefined,
});

export const setTransformControl = (controlState?: Partial<TransformControlState> | null): void => {
  state.objectName = controlState?.objectName;
  state.onAfterTranslate = controlState?.onAfterTranslate;
  state.onAfterRotate = controlState?.onAfterRotate;
  state.onBeforeActivate = controlState?.onBeforeActivate;
};

export const hideTransformControl = (): void => setTransformControl();

const frustum = new Frustum();

export const TransformControl: FC<{
  onMouseDown: EventHandler;
  onMouseUp: (event: Event, success: boolean) => void;
}> = ({ onMouseDown: onMouseDownProp, onMouseUp: onMouseUpProp }) => {
  const { onAfterTranslate, onAfterRotate, onBeforeActivate, objectName } = useSnapshot(state);
  const { scene, invalidate, camera, gl } = useThree();
  const [translatePressed, rotatePressed] = useKeys('t', 'r');

  // Track the controls instance using a Ref since it is a mutable Three.js object.
  // We use isMounted state to notify effects when the control instance is ready.
  const transformCtrlRef = useRef<TransformControlsPublic | null>(null);
  const [isMounted, setIsMounted] = useState(false);

  // Cache transformations on mouseDown to physically restore them if validation fails.
  const startPosition = useRef(new Vector3());
  const startRotation = useRef(new Euler());
  const startScale = useRef(new Vector3());

  // 1. Keep the camera reference updated on the controls
  useEffect(() => {
    const transformCtrl = transformCtrlRef.current;
    if (!transformCtrl) return;
    transformCtrl.camera = camera;
  }, [camera, isMounted]);

  // 2. Mounting, scene registration, and change listener binding
  useEffect(() => {
    const ctrl = new TransformControls(camera, gl.domElement) as unknown as TransformControlsPublic;
    ctrl.name = Object3dNames.Transform;
    ctrl.enabled = false;
    ctrl.visible = false;

    const handleChange = () => invalidate();
    addListener(ctrl, 'change', handleChange);

    scene.add(ctrl);
    transformCtrlRef.current = ctrl;
    setIsMounted(true);

    return () => {
      removeListener(ctrl, 'change', handleChange);
      ctrl.detach();
      scene.remove(ctrl);
      ctrl.dispose();
      transformCtrlRef.current = null;
      setIsMounted(false);
    };
  }, [scene, camera, gl.domElement, invalidate]);

  // 3. Assign Object with direct properties to prevent .attach() from auto-enabling visibility
  useEffect(() => {
    const transformCtrl = transformCtrlRef.current;
    if (!transformCtrl) return;
    if (!objectName) {
      transformCtrl.object = undefined;
      transformCtrl.enabled = false;
      transformCtrl.visible = false;
      invalidate();
      return;
    }

    const nextObject = scene.getObjectByName(objectName);
    if (!nextObject) {
      transformCtrl.object = undefined;
      transformCtrl.enabled = false;
      transformCtrl.visible = false;
      invalidate();
      return;
    }

    if (transformCtrl.object !== nextObject) {
      transformCtrl.object = nextObject;
      transformCtrl.position.set(0, 0, 0);
      invalidate();
    }
  }, [isMounted, scene, objectName, invalidate]);

  // Synchronously detach the control object if it has been unmounted from the scene graph
  useFrame(() => {
    const transformCtrl = transformCtrlRef.current;
    if (transformCtrl && transformCtrl.object && !(transformCtrl.object as Object3D).parent) {
      transformCtrl.detach();
      transformCtrl.enabled = false;
      transformCtrl.visible = false;
      invalidate();
    }
  });

  // 4. Simple & flat toggle control logic
  useEffect(() => {
    const transformCtrl = transformCtrlRef.current;
    if (!transformCtrl) return;
    const showTool = (show: boolean) => {
      transformCtrl.enabled = show;
      transformCtrl.visible = show;
      invalidate();
    };

    if (!onAfterTranslate && !onAfterRotate) {
      showTool(false);
      return;
    }

    const isCurrentlyTranslate = transformCtrl.visible && transformCtrl.mode === 'translate';
    const isCurrentlyRotate = transformCtrl.visible && transformCtrl.mode === 'rotate';

    // Toggle off if clicking same mode, or switching to an unsupported handler mode
    if (
      (isCurrentlyTranslate && translatePressed) ||
      (isCurrentlyRotate && rotatePressed) ||
      (isCurrentlyRotate && !onAfterRotate) ||
      (isCurrentlyTranslate && !onAfterTranslate)
    ) {
      showTool(false);
      return;
    }

    // Activate modes based on keyboard shortcuts
    if (translatePressed && onAfterTranslate) {
      if (onBeforeActivate) {
        onBeforeActivate('translate', () => {
          transformCtrl.mode = 'translate';
          showTool(true);
        });
      } else {
        transformCtrl.mode = 'translate';
        showTool(true);
      }
    } else if (rotatePressed && onAfterRotate) {
      if (onBeforeActivate) {
        onBeforeActivate('rotate', () => {
          transformCtrl.mode = 'rotate';
          showTool(true);
        });
      } else {
        transformCtrl.mode = 'rotate';
        showTool(true);
      }
    }
  }, [
    isMounted,
    translatePressed,
    rotatePressed,
    onAfterTranslate,
    onAfterRotate,
    onBeforeActivate,
    invalidate,
  ]);

  // 5. Robust interaction handlers (with visual rollback on validation failure)
  useEffect(() => {
    const transformCtrl = transformCtrlRef.current;
    if (!transformCtrl) return;
    const handleMouseDown = (e: Event) => {
      if (transformCtrl.object) {
        const obj = transformCtrl.object as Object3D;
        startPosition.current.copy(obj.position);
        startRotation.current.copy(obj.rotation);
        startScale.current.copy(obj.scale);
      }
      onMouseDownProp?.(e);
    };

    const handleMouseUp = (e: Event) => {
      if (!transformCtrl.object || !transformCtrl.visible) {
        onMouseUpProp?.(e, true);
        return;
      }

      const MAX_TRANSFORM_DISTANCE_M = 200;

      if (transformCtrl.mode === 'translate') {
        const obj = transformCtrl.object as Object3D;
        const currentPosition = obj.position;
        const distance = startPosition.current.distanceTo(currentPosition);

        // Standard public camera/frustum setup
        const objectWorldPosition = new Vector3();
        obj.getWorldPosition(objectWorldPosition);

        camera.updateMatrixWorld();
        camera.updateProjectionMatrix();
        frustum.setFromProjectionMatrix(
          new Matrix4().multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse),
        );

        if (distance > MAX_TRANSFORM_DISTANCE_M) {
          // Physically rollback visual position of the mesh
          obj.position.copy(startPosition.current);
          obj.updateMatrix();
          obj.updateMatrixWorld();
          transformCtrl.reset();
          invalidate();
          errorBox(
            `Objects cannot be moved more than ${MAX_TRANSFORM_DISTANCE_M}m at once. We made this restriction in order to prevent a glitch with the transform tool. \nDon't worry, ${obj.name} will not be translated!`,
          );
          onMouseUpProp?.(e, false);
          return;
        }

        if (!frustum.containsPoint(objectWorldPosition)) {
          // Physically rollback visual position of the mesh
          obj.position.copy(startPosition.current);
          obj.updateMatrix();
          obj.updateMatrixWorld();
          transformCtrl.reset();
          invalidate();
          errorBox(
            `Transform control is outside viewport. This could have been due to a glitch. \nDon't worry, ${obj.name} will not be translated!`,
          );
          onMouseUpProp?.(e, false);
          return;
        }

        // Successfully commit translation
        onAfterTranslate?.(e);
      } else if (transformCtrl.mode === 'rotate') {
        // Successfully commit rotation
        onAfterRotate?.(e);
      }

      onMouseUpProp?.(e, true);
    };

    addListener(transformCtrl, 'mouseDown', handleMouseDown);
    addListener(transformCtrl, 'mouseUp', handleMouseUp);

    return () => {
      removeListener(transformCtrl, 'mouseDown', handleMouseDown);
      removeListener(transformCtrl, 'mouseUp', handleMouseUp);
    };
  }, [
    isMounted,
    camera,
    invalidate,
    onMouseDownProp,
    onMouseUpProp,
    onAfterTranslate,
    onAfterRotate,
  ]);

  return null;
};
